import { clsx } from 'clsx';
import { FC, MouseEventHandler, ReactNode } from 'react';

import { styled, Theme } from '@mui/material/styles';

import useMediaQuery from '@mui/material/useMediaQuery';
import PenFillIcon from '@o2/icons/PenFill';
import IconButton from '@o2/ui/IconButton';
import Stack from '@o2/ui/Stack';
import Typography from '@o2/ui/Typography';
import Box from '@o2/ui/Box';

import { useSubheaderUtilityClasses } from './subheaderClasses';

export interface TitleProps {
  /**
   * If a string, it is converted to a (Typography) heading, unless `disableStyling` is true. If you need customization, pass a
   * React component instead.
   */
  children: ReactNode;

  /**
   * Disable styling the passed children as a Title (heading).
   *
   * Defaults to false (= styles the children) if children is a string,
   * true otherwise (if you pass a ReactElement, for example, it will not be styled).
   *
   * Useful to override for example when composing the title from several `<span/>`s.
   */
  disableStyling?: boolean;

  /**
   * Show a pencil icon, calling back `onEditClick` when it's clicked. This component doesn't handle the editing itself.
   */
  editable?: boolean;

  /**
   * OnClick handler for the edit icon, if enabled with by setting `editable`.
   */
  onEditClick?: MouseEventHandler;
}

const SubheaderEditIconButton = styled(IconButton, {
  name: 'O2Subheader',
  slot: 'EditIcon',
})(({ theme }) => ({
  color: theme.palette.text.primary,
  padding: '7px',
  marginLeft: '3px',
}));

const Title: FC<TitleProps> = ({
  children,
  disableStyling = typeof children !== 'string',
  editable,
  onEditClick,
  ...props
}) => {
  const utilityClasses = useSubheaderUtilityClasses();

  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const titleEl = disableStyling ? (
    children
  ) : (
    <Typography variant={mdUp ? 'h2' : 'h1'} component="h1">
      {children}
    </Typography>
  );

  return (
    <Stack
      flexShrink={0}
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      className={clsx(utilityClasses.title)}
      {...props}
    >
      {titleEl}
      {editable && (
        <Box>
          <SubheaderEditIconButton onClick={onEditClick}>
            <PenFillIcon />
          </SubheaderEditIconButton>
        </Box>
      )}
    </Stack>
  );
};

Title.displayName = 'Subheader.Title';

export default Title;
